/**
 * useSortableAutoScroll - Frame-driven auto-scroll for drag-to-reorder.
 *
 * While a drag is active and the finger sits near the top/bottom edge of the
 * scroll viewport, this scrolls the parent ScrollView and accumulates the moved
 * distance into `autoScrollDelta` so the dragged row keeps following the finger
 * and the hover index keeps advancing past off-screen rows.
 *
 * Compatible with react-native-reanimated v4 (useFrameCallback + scrollTo).
 */

import {
  scrollTo,
  useFrameCallback,
  useScrollViewOffset,
  type AnimatedRef,
  type FrameCallback,
  type SharedValue,
} from 'react-native-reanimated';
import type Animated from 'react-native-reanimated';

/** Distance from an edge (px) at which auto-scroll begins, ramping to MAX_SPEED. */
const EDGE_THRESHOLD = 80;
/** Maximum scroll speed in px per frame. */
const MAX_SPEED = 9;

type AutoScrollConfig = {
  scrollRef: AnimatedRef<Animated.ScrollView>;
  activeIndex: SharedValue<number>;
  absoluteY: SharedValue<number>;
  autoScrollDelta: SharedValue<number>;
  /** Window Y of the scroll viewport's top edge. */
  viewportTop: number;
  /** Window Y of the scroll viewport's bottom edge. */
  viewportBottom: number;
  /** Worklet that re-derives dragY + hover index after a scroll step. */
  onScroll: () => void;
};

export function useSortableAutoScroll({
  scrollRef,
  activeIndex,
  absoluteY,
  autoScrollDelta,
  viewportTop,
  viewportBottom,
  onScroll,
}: AutoScrollConfig): FrameCallback {
  const scrollOffset = useScrollViewOffset(scrollRef);

  const frame = useFrameCallback(() => {
    'worklet';
    if (activeIndex.value < 0) {
      return;
    }

    const y = absoluteY.value;
    let speed = 0;
    if (y < viewportTop + EDGE_THRESHOLD) {
      const intensity = Math.min(1, (viewportTop + EDGE_THRESHOLD - y) / EDGE_THRESHOLD);
      speed = -MAX_SPEED * intensity;
    } else if (y > viewportBottom - EDGE_THRESHOLD) {
      const intensity = Math.min(1, (y - (viewportBottom - EDGE_THRESHOLD)) / EDGE_THRESHOLD);
      speed = MAX_SPEED * intensity;
    }

    if (speed === 0) {
      return;
    }
    // Don't keep accumulating past the top — scrollTo clamps but the delta would drift.
    if (speed < 0 && scrollOffset.value <= 0) {
      return;
    }

    scrollTo(scrollRef, 0, scrollOffset.value + speed, false);
    autoScrollDelta.value += speed;
    onScroll();
  }, false);

  return frame;
}
